import { c as _c } from "react/compiler-runtime";
import type { ErrorDetail } from '@onefootprint/request-types/dashboard';
import { Dialog, InlineAlert, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useTranslateCategory } from '@/components/playbook-builder/hooks/use-translate-category';
import type { ApplyEditMutation } from '../base-node';
import { ReadOnlyBranchDisplay } from './read-only-branch';
import type { DefaultRuleSet, RecommendedAction } from './rule-suggestion-context.types';
type AddDefaultRulesVariant = 'conditional' | 'rules';
type AddDefaultRulesProps = {
  ruleSet: DefaultRuleSet;
  nodeId: string;
  mutation: ApplyEditMutation;
  error: ErrorDetail;
  type: 'error' | 'warning' | 'info';
  variant: AddDefaultRulesVariant;
};
export const AddDefaultRules = t0 => {
  const $ = _c(59);
  const {
    error,
    type,
    ruleSet,
    nodeId,
    mutation,
    variant
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rules.addDefaultRules"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    isAnyComponentDirty
  } = useDirty();
  const [open, setIsOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsOpen(true);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleShowConfirmation = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsOpen(false);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleClose = t3;
  let t4;
  if ($[3] !== mutation || $[4] !== nodeId || $[5] !== ruleSet.category || $[6] !== ruleSet.key) {
    t4 = () => {
      mutation.mutateAsync({
        kind: "update_node",
        nodeId,
        nodeData: {
          kind: "add_default_rules",
          category: ruleSet.category,
          key: ruleSet.key
        }
      });
    };
    $[3] = mutation;
    $[4] = nodeId;
    $[5] = ruleSet.category;
    $[6] = ruleSet.key;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleConfirmAddRules = t4;
  const categoryStr = useTranslateCategory(ruleSet.category, ruleSet.key);
  let t5;
  if ($[8] !== categoryStr || $[9] !== isAnyComponentDirty || $[10] !== t) {
    t5 = isAnyComponentDirty ? t(_temp, {
      categoryStr
    }) : undefined;
    $[8] = categoryStr;
    $[9] = isAnyComponentDirty;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const disabledTooltipText = t5;
  const isDisabled = isAnyComponentDirty || mutation.isPending;
  let t6;
  if ($[12] !== t) {
    t6 = t(_temp2);
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t(_temp3);
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== handleConfirmAddRules || $[17] !== mutation.isPending || $[18] !== t7) {
    t8 = {
      label: t7,
      loading: mutation.isPending,
      onClick: handleConfirmAddRules
    };
    $[16] = handleConfirmAddRules;
    $[17] = mutation.isPending;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t(_temp4);
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== mutation.isPending || $[23] !== t9) {
    t10 = {
      label: t9,
      disabled: mutation.isPending,
      onClick: handleClose
    };
    $[22] = mutation.isPending;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== ruleSet || $[26] !== variant) {
    t11 = <AddDefaultRulesDialog ruleSet={ruleSet} variant={variant} />;
    $[25] = ruleSet;
    $[26] = variant;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== open || $[29] !== t10 || $[30] !== t11 || $[31] !== t6 || $[32] !== t8) {
    t12 = <Dialog isConfirmation={true} open={open} onClose={handleClose} title={t6} primaryButton={t8} secondaryButton={t10}>{t11}</Dialog>;
    $[28] = open;
    $[29] = t10;
    $[30] = t11;
    $[31] = t6;
    $[32] = t8;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== error.message) {
    t13 = <span>{error.message}</span>;
    $[34] = error.message;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = !disabledTooltipText;
  const t15 = ruleSet.category;
  const t16 = !isDisabled;
  let t17;
  if ($[36] !== isDisabled || $[37] !== t16) {
    t17 = cx(["ml-1 underline cursor-pointer", {
      "hover:opacity-70 active:opacity-85": t16,
      "opacity-50 cursor-not-allowed": isDisabled
    }]);
    $[36] = isDisabled;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = isAnyComponentDirty || mutation.isPending;
  let t19;
  if ($[39] !== t) {
    t19 = t(_temp5);
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== ruleSet.category || $[42] !== t17 || $[43] !== t18 || $[44] !== t19) {
    t20 = <button type="button" key={t15} onClick={handleShowConfirmation} className={t17} disabled={t18}>{t19}</button>;
    $[41] = ruleSet.category;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== disabledTooltipText || $[47] !== t14 || $[48] !== t20) {
    t21 = <Tooltip content={disabledTooltipText} disabled={t14}>{t20}</Tooltip>;
    $[46] = disabledTooltipText;
    $[47] = t14;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t13 || $[51] !== t21) {
    t22 = <div className="text-warning text-body-2">{t13}{t21}</div>;
    $[50] = t13;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22 || $[54] !== type) {
    t23 = <InlineAlert variant={type}>{t22}</InlineAlert>;
    $[53] = t22;
    $[54] = type;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t12 || $[57] !== t23) {
    t24 = <>{t12}{t23}</>;
    $[56] = t12;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
};
const AddDefaultRulesDialog = t0 => {
  const $ = _c(24);
  const {
    ruleSet,
    variant
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "rules.addDefaultRules.confirm"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t: tConditional
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "rules.addDefaultRulesGroups.confirm"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tGroups
  } = useTranslation("playbook-builder", t2);
  const t = variant === "conditional" ? tConditional : tGroups;
  const categoryStr = useTranslateCategory(ruleSet.category, ruleSet.key);
  let t3;
  if ($[2] !== t) {
    t3 = t(_temp6);
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t(_temp7);
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      fail: t3,
      manual_review: t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const actionTranslations = t5;
  let t6;
  if ($[9] !== categoryStr || $[10] !== t) {
    t6 = t(_temp8, {
      categoryStr
    });
    $[9] = categoryStr;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="text-body-2">{t6}</div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== actionTranslations || $[15] !== ruleSet.defaultRules || $[16] !== t) {
    let t9;
    if ($[18] !== actionTranslations || $[19] !== t) {
      t9 = branch => <ReadOnlyBranchDisplay key={branch.action} branchNameLabel={t(_temp9)} branchName={actionTranslations[branch.action]} rules={branch.rules} itemsLabel={t(_temp10)} />;
      $[18] = actionTranslations;
      $[19] = t;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = ruleSet.defaultRules.map(t9);
    $[14] = actionTranslations;
    $[15] = ruleSet.defaultRules;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = <div className="flex flex-col gap-4">{t7}{t8}</div>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
function _temp($) {
  return $.disabledTooltip;
}
function _temp2($_0) {
  return $_0.confirm.title;
}
function _temp3($_1) {
  return $_1.confirm.confirm;
}
function _temp4($_2) {
  return $_2.confirm.cancel;
}
function _temp5($_3) {
  return $_3.cta;
}
function _temp6($) {
  return $.action.fail;
}
function _temp7($_0) {
  return $_0.action.manual_review;
}
function _temp8($_1) {
  return $_1.description;
}
function _temp9($_2) {
  return $_2.addLabel;
}
function _temp10($_3) {
  return $_3.itemsLabel;
}